import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, X, Send, Loader2, User, Headphones, ArrowLeft, Plus } from "lucide-react";
import { useLanguage } from "@/hooks/use-language";

interface LiveMessage {
  id: number;
  direction: string;
  body: string;
  senderName: string | null;
  channel: string;
  sentAt: string;
}

interface Conversation {
  id: number;
  guestName: string;
  status: string;
  lastMessageAt: string;
}

interface GuestLiveChatProps {
  slug: string;
  token: string;
  apartmentName: string;
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
}

export default function GuestLiveChat({
  slug, token, apartmentName, guestName: initialGuestName, guestEmail, guestPhone
}: GuestLiveChatProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConvoId, setActiveConvoId] = useState<number | null>(null);
  const [messages, setMessages] = useState<LiveMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [guestName, setGuestName] = useState(initialGuestName || "");
  const [showNameInput, setShowNameInput] = useState(!initialGuestName);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { lang } = useLanguage();
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen, showNameInput]);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await fetch(`/api/g/${slug}/live-chat?t=${token}`);
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
        if (data.length === 1 && !activeConvoId) {
          setActiveConvoId(data[0].id);
        }
      }
    } catch {}
  }, [slug, token, activeConvoId]);

  const fetchMessages = useCallback(async () => {
    if (!activeConvoId) return;
    try {
      const res = await fetch(`/api/g/${slug}/live-chat/${activeConvoId}/messages?t=${token}`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch {}
  }, [slug, token, activeConvoId]);

  useEffect(() => {
    if (isOpen) {
      fetchConversations();
    }
  }, [isOpen, fetchConversations]);

  useEffect(() => {
    if (activeConvoId && isOpen) {
      fetchMessages();
      pollRef.current = setInterval(fetchMessages, 5000);
      return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }
  }, [activeConvoId, isOpen, fetchMessages]);

  const [error, setError] = useState<string | null>(null);

  const startConversation = async (firstMessage: string) => {
    if (!guestName.trim() || !firstMessage.trim()) return;
    setIsSending(true);
    setError(null);
    try {
      const res = await fetch(`/api/g/${slug}/live-chat?t=${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          guestName: guestName.trim(),
          guestEmail: guestEmail || undefined,
          guestPhone: guestPhone || undefined,
          message: firstMessage.trim(),
        }),
      });
      if (res.ok) {
        const convo = await res.json();
        setActiveConvoId(convo.id);
        setConversations(prev => [...prev, convo]);
        setShowNameInput(false);
        setInput("");
        setTimeout(fetchMessages, 300);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.message || (lang === "en" ? "Failed to start conversation" : "Error al iniciar conversación"));
      }
    } catch {
      setError(lang === "en" ? "Connection error. Please try again." : "Error de conexión. Intente de nuevo.");
    } finally {
      setIsSending(false);
    }
  };

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || isSending) return;

    if (!activeConvoId) {
      if (!guestName.trim()) {
        setShowNameInput(true);
        return;
      }
      await startConversation(trimmed);
      return;
    }

    setIsSending(true);
    setInput("");
    setMessages(prev => [...prev, {
      id: Date.now(),
      direction: "inbound",
      body: trimmed,
      senderName: guestName || "Guest",
      channel: "internal",
      sentAt: new Date().toISOString(),
    }]);

    try {
      const res = await fetch(`/api/g/${slug}/live-chat/${activeConvoId}/messages?t=${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: trimmed, guestName: guestName || "Guest" }),
      });
      if (!res.ok) {
        setError(lang === "en" ? "Failed to send message" : "Error al enviar mensaje");
      }
      fetchMessages();
    } catch {
      setError(lang === "en" ? "Connection error" : "Error de conexión");
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (showNameInput && guestName.trim()) {
        setShowNameInput(false);
      } else {
        sendMessage();
      }
    }
  };

  const unreadCount = 0;

  return (
    <>
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-36 right-4 z-50 rounded-full w-12 h-12 shadow-lg bg-green-600 hover:bg-green-700"
          size="icon"
          data-testid="button-open-live-chat"
        >
          <Headphones className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">{unreadCount}</span>
          )}
        </Button>
      )}

      {isOpen && (
        <div className="fixed bottom-36 right-4 left-4 z-50 sm:left-auto sm:w-[380px]" data-testid="container-live-chat">
          <Card className="flex flex-col h-[420px] shadow-xl overflow-hidden">
            <div className="flex items-center justify-between gap-2 px-4 py-3 bg-green-600 text-white rounded-t-md">
              <div className="flex items-center gap-2 min-w-0">
                {activeConvoId && conversations.length > 1 && (
                  <Button size="icon" variant="ghost" className="text-white h-7 w-7"
                    onClick={() => { setActiveConvoId(null); setMessages([]); }}>
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                )}
                <Headphones className="w-5 h-5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate" data-testid="text-live-chat-title">
                    {lang === "en" ? "Live Support" : "Soporte en Vivo"}
                  </p>
                  <p className="text-[10px] opacity-80 truncate">{apartmentName}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="text-[9px] border-white/30 text-white px-1.5">
                  {lang === "en" ? "Live" : "En Vivo"}
                </Badge>
                <Button size="icon" variant="ghost" className="text-white h-8 w-8"
                  onClick={() => setIsOpen(false)} data-testid="button-close-live-chat">
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-background">
              {showNameInput ? (
                <div className="text-center py-6">
                  <Headphones className="w-10 h-10 mx-auto text-green-500 mb-3" />
                  <p className="text-sm font-medium mb-1">
                    {lang === "en" ? "Chat with our team" : "Chatea con nuestro equipo"}
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    {lang === "en" ? "Enter your name to start" : "Ingresa tu nombre para comenzar"}
                  </p>
                  <Input
                    ref={inputRef}
                    placeholder={lang === "en" ? "Your name" : "Tu nombre"}
                    value={guestName}
                    onChange={e => setGuestName(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && guestName.trim()) setShowNameInput(false); }}
                    className="max-w-xs mx-auto mb-3"
                    data-testid="input-guest-name-chat"
                  />
                  <Button size="sm" className="bg-green-600 hover:bg-green-700"
                    disabled={!guestName.trim()} onClick={() => setShowNameInput(false)}>
                    {lang === "en" ? "Start Chat" : "Iniciar Chat"}
                  </Button>
                </div>
              ) : !activeConvoId && conversations.length === 0 ? (
                <div className="text-center py-6">
                  <MessageCircle className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">
                    {lang === "en" ? "Send a message to start the conversation" : "Envía un mensaje para comenzar la conversación"}
                  </p>
                </div>
              ) : !activeConvoId && conversations.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground mb-2">
                    {lang === "en" ? "Your conversations" : "Tus conversaciones"}
                  </p>
                  {conversations.map(conv => (
                    <button key={conv.id} className="w-full text-left p-3 rounded-lg border hover:border-green-300 transition-colors"
                      onClick={() => setActiveConvoId(conv.id)} data-testid={`convo-${conv.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{conv.guestName}</span>
                        <Badge variant={conv.status === "open" ? "default" : "secondary"} className="text-[9px]">{conv.status}</Badge>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {conv.lastMessageAt ? new Date(conv.lastMessageAt).toLocaleString() : ""}
                      </p>
                    </button>
                  ))}
                  <Button variant="outline" size="sm" className="w-full mt-2"
                    onClick={() => { setActiveConvoId(null); setMessages([]); }}>
                    <Plus className="w-3 h-3 mr-1" /> {lang === "en" ? "New Conversation" : "Nueva Conversación"}
                  </Button>
                </div>
              ) : (
                <>
                  {error && (
                    <div className="text-center py-2 px-3 bg-red-50 dark:bg-red-950 rounded-md text-xs text-red-600 dark:text-red-400" data-testid="text-chat-error">
                      {error}
                      <Button variant="ghost" size="sm" className="ml-2 h-5 text-xs" onClick={() => setError(null)}>✕</Button>
                    </div>
                  )}
                  {messages.map((msg) => (
                    <div key={msg.id} className={`flex gap-2 ${msg.direction === "inbound" ? "justify-end" : "justify-start"}`}
                      data-testid={`live-msg-${msg.id}`}>
                      {msg.direction === "outbound" && (
                        <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Headphones className="w-4 h-4 text-green-600" />
                        </div>
                      )}
                      <div className={`max-w-[80%] px-3 py-2 rounded-md text-sm whitespace-pre-wrap ${
                        msg.direction === "inbound"
                          ? "bg-green-600 text-white"
                          : "bg-muted"
                      }`}>
                        {msg.senderName && msg.direction === "outbound" && (
                          <p className="text-[10px] font-medium text-muted-foreground mb-0.5">{msg.senderName}</p>
                        )}
                        {msg.body}
                        <p className={`text-[9px] mt-0.5 ${msg.direction === "inbound" ? "text-white/60" : "text-muted-foreground"}`}>
                          {msg.sentAt ? new Date(msg.sentAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                        </p>
                      </div>
                      {msg.direction === "inbound" && (
                        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                          <User className="w-4 h-4 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>

            {!showNameInput && (
              <div className="p-3 border-t bg-background flex items-center gap-2">
                <Input
                  ref={inputRef}
                  placeholder={lang === "en" ? "Type your message..." : "Escribe tu mensaje..."}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isSending}
                  data-testid="input-live-chat-message"
                />
                <Button
                  size="icon"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={sendMessage}
                  disabled={!input.trim() || isSending}
                  data-testid="button-send-live-message"
                >
                  {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            )}
          </Card>
        </div>
      )}
    </>
  );
}

